import type { MouseEventHandler, ReactNode } from 'react';
import styles from './CalendarCell.module.css';

/**
 * Figma: `_Calendar cell` (123:2568) — "Internal sub-component representing a
 * single day in the calendar grid. Variants: Type (Default, Active, Selected) ×
 * State (Default, Hover, Disabled). Boolean props: Left connector, Right
 * connector (for range highlighting), Active dot."
 *
 * That description used to add "Font: Inter Regular 14px"; the 2026-08-31 type
 * re-cut moved the cell to `font-size/sm` (12) on `line-height/md` (18), and the
 * annotation now states the tokens rather than a size. 40x40 on `radius/full`
 * is unchanged.
 *
 * `Type` is what the day is: `active` is inside the chosen range, `selected` is
 * an endpoint of it. `State` is not a prop — hover is CSS and disabled is the
 * real `disabled` attribute.
 *
 * The `Active dot` boolean in the component set is referenced by no layer in any
 * of the nine variants, so it has no implementation here. See plan.md §7.
 */
export type CalendarCellType = 'default' | 'active' | 'selected';

/** Force a state for documentation and visual regression only. */
export type CalendarCellState = 'default' | 'hover';

export type CalendarCellProps = {
  children: ReactNode;
  /** Figma `Type`. */
  type?: CalendarCellType;
  /**
   * Figma `Left connector` / `Right connector` — the wedges that bridge this
   * day's circle to its neighbour's so a range reads as one continuous band.
   * A connector-less side is where the band gets its rounded cap.
   */
  connectLeft?: boolean;
  connectRight?: boolean;
  /** Figma `State=Disabled`. */
  disabled?: boolean;
  /**
   * Weekday headers ("Mo", "Tu") are drawn with this same component in Figma but
   * are not days — pass `false` and they render as static text.
   */
  interactive?: boolean;
  /**
   * Accessible name. The visible content is a bare number, which says nothing
   * on its own — not in Figma, which has no concept of an accessible name.
   */
  label?: string;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  /**
   * For the roving tabindex the month grid runs — one day is tabbable and the
   * arrow keys move between the rest. Not in Figma.
   */
  tabIndex?: number;
  state?: CalendarCellState;
  className?: string;
};

const typeClass: Record<CalendarCellType, string> = {
  default: styles.default,
  active: styles.active,
  selected: styles.selected,
};

export function CalendarCell({
  children,
  type = 'default',
  connectLeft = false,
  connectRight = false,
  disabled = false,
  interactive = true,
  label,
  onClick,
  tabIndex,
  state,
  className,
}: CalendarCellProps) {
  const inRange = type !== 'default';

  const classes = [
    styles.cell,
    typeClass[type],
    inRange ? styles.band : null,
    inRange && !connectLeft ? styles.capLeft : null,
    inRange && !connectRight ? styles.capRight : null,
    disabled ? styles.disabled : null,
    /*
     * Hover is live only when nothing is forced and the day can be picked, so
     * `:hover` reacts in the app and stays inert in a states story. Figma has no
     * disabled-and-hovered variant, so a forced hover on a disabled day is
     * ignored — same rule as `Button`.
     */
    !disabled && state === 'hover' ? styles.hover : null,
    !disabled && state === undefined ? styles.interactive : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const content = <span className={styles.content}>{children}</span>;

  if (!interactive) {
    return (
      <span className={classes} aria-label={label}>
        {content}
      </span>
    );
  }

  return (
    <button
      className={classes}
      type="button"
      disabled={disabled}
      onClick={onClick}
      tabIndex={tabIndex}
      aria-label={label}
    >
      {content}
    </button>
  );
}
